import { create } from 'zustand';
import { GraphNode, GraphEdge, FilterState, Provider, NodeType } from '../types/graph';
import type { CloudSaasCorrelation, CloudEntityBlastRadius } from '../services/cloudApi';

interface ExpansionPanelState {
  parentId: string;
  childIds: string[];
}

interface QueryResults {
  '1': string[]; // Most Risky Entities
  '2': string[]; // Dormant Users
  '3': string[]; // Dormant Service Accounts
  '4': string[]; // Most Active Entities
  '5': string[]; // Most Used Resources
  '6': string[]; // Over Privileged Accounts
  '7': string[]; // Recent Anomalies
}

interface GraphStore {
  nodes: GraphNode[];
  edges: GraphEdge[];
  allNodes: GraphNode[];
  allEdges: GraphEdge[];
  selectedNodes: Set<string>;
  selectedEdges: Set<string>;
  inspectorOpen: boolean;
  viewFilterOpen: boolean;
  viewFilter: 'most-risky' | 'recent-alerts' | 'most-active' | 'recently-active' | 'critical';
  filters: FilterState;
  theme: 'light' | 'dark';
  activeConnectionType: string;
  activeConnectionSubFilter: string | null;
  expandedNodes: Set<string>;
  collapsedChildren: Map<string, string[]>;
  currentGraphPage: number;
  maxGraphsPerPage: number;
  totalAvailableGraphs: number;
  focusMode: boolean;
  focusedGraphId: string | null;
  // Progressive expansion state
  activeExpansionPanel: ExpansionPanelState | null;
  promotedChildren: Map<string, Set<string>>; // parentId -> Set of childIds shown as full nodes
  // Search state
  searchQuery: string;
  searchResults: string[]; // Node IDs matching search
  highlightedNodes: Set<string>; // Currently highlighted node IDs
  isSearchFocused: boolean;
  // Security questions state
  queryResults: QueryResults | null;
  activeSecurityQuestion: number | null;
  // Mode state
  activeMode: 'activity' | 'correlations' | 'blast-radius';
  isModeLoading: boolean;
  // Correlations & Blast Radius raw data
  correlationsData: CloudSaasCorrelation[];
  blastRadiusData: CloudEntityBlastRadius[];
  // Persisted activity nodes for reuse across modes
  activityAllNodes: GraphNode[];
  activityAllEdges: GraphEdge[];
  // Beautify state
  triggerBeautify: number; // increment to trigger beautify
  isBeautified: boolean; // whether graph is currently in beautified layout

  setGraphData: (nodes: GraphNode[], edges: GraphEdge[], queryResults?: QueryResults) => void;
  selectNode: (nodeId: string) => void;
  selectEdge: (edgeId: string) => void;
  clearSelection: () => void;
  toggleInspector: () => void;
  toggleViewFilter: () => void;
  setViewFilter: (filter: 'most-risky' | 'recent-alerts' | 'most-active' | 'recently-active' | 'critical') => void;
  setFilters: (filters: Partial<FilterState>) => void;
  toggleProvider: (provider: Provider) => void;
  toggleNodeType: (nodeType: NodeType) => void;
  toggleTheme: () => void;
  setActiveConnectionType: (type: string, subFilter?: string) => void;
  expandNode: (nodeId: string) => void;
  collapseAll: () => void;
  collapseStep: (nodeId: string) => void; // Collapse only the children of a specific node
  expandCollapsedGroup: (parentId: string) => void;
  nextGraphPage: () => void;
  previousGraphPage: () => void;
  canGoNext: () => boolean;
  canGoPrevious: () => boolean;
  toggleFocusMode: () => void;
  setFocusedGraph: (graphId: string | null) => void;
  // Progressive expansion actions
  openExpansionPanel: (parentId: string, childIds: string[]) => void;
  closeExpansionPanel: () => void;
  promoteChild: (parentId: string, childId: string) => void;
  // Search actions
  setSearchQuery: (query: string) => void;
  setSearchFocused: (focused: boolean) => void;
  clearSearch: () => void;
  navigateToNode: (nodeId: string) => void;
  zoomToHighlightedNodes: () => void;
  triggerZoomToNode: string | null;
  setTriggerZoomToNode: (nodeId: string | null) => void;
  // Security questions actions
  applySecurityQuestion: (questionIndex: number) => void;
  clearSecurityQuestion: () => void;
  // Mode actions
  setActiveMode: (mode: 'activity' | 'correlations' | 'blast-radius') => void;
  setModeLoading: (loading: boolean) => void;
  // Correlations & Blast Radius actions
  setCorrelationsData: (data: CloudSaasCorrelation[]) => void;
  setBlastRadiusData: (data: CloudEntityBlastRadius[]) => void;
  setModeGraphData: (nodes: GraphNode[], edges: GraphEdge[]) => void;
  getActivityAllNodes: () => GraphNode[];
  // Beautify actions
  triggerBeautifyLayout: () => void;
  setBeautified: (val: boolean) => void;
}

const getNumberProp = (node: GraphNode, key: string) => {
  const value = Number(node.props?.[key]);
  return Number.isFinite(value) ? value : 0;
};

const getDateProp = (node: GraphNode, key: string) => {
  const value = node.props?.[key];
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const compareByAlertsAndActivity = (a: GraphNode, b: GraphNode) =>
  getNumberProp(b, 'alertCount') - getNumberProp(a, 'alertCount') ||
  getNumberProp(b, 'activity') - getNumberProp(a, 'activity') ||
  a.label.localeCompare(b.label);

// Helper function to sort graph IDs based on viewFilter
const getSortedGraphIds = (
  nodes: GraphNode[],
  type: string,
  viewFilter: string,
  subFilter?: string
): string[] => {
  // Get root nodes based on type
  let rootNodes = nodes.filter(n => {
    if (type === 'users') {
      return n.type === 'user' && n.props?.graphId?.startsWith('user-');
    } else if (type === 'sa') {
      if (n.type !== 'service_account' || !n.props?.graphId?.startsWith('sa-')) return false;
      if (subFilter === 'user-managed') return n.props?.isUserManaged === true;
      if (subFilter === 'cloud-managed') return n.props?.isUserManaged === false;
      return true;
    } else if (type === 'resources') {
      if (n.type !== 'resource' || !n.props?.graphId?.startsWith('resource-center-')) return false;
      // Sub-filters differ by cloud:
      // - GCP/Azure: virtual-machine | database | storage-bucket
      // - AWS: ec2 | rds | s3
      if (subFilter === 'vm') return n.props?.resourceCategory === 'virtual-machine';
      if (subFilter === 'database') return n.props?.resourceCategory === 'database';
      if (subFilter === 'storage') return n.props?.resourceCategory === 'storage-bucket';

      if (subFilter === 'ec2') return n.props?.resourceCategory === 'ec2' || n.props?.resourceCategory === 'virtual-machine';
      if (subFilter === 'rds') return n.props?.resourceCategory === 'rds' || n.props?.resourceCategory === 'database';
      if (subFilter === 's3') return n.props?.resourceCategory === 's3' || n.props?.resourceCategory === 'storage-bucket';

      return true;
    }
    return false;
  });

  // Sort based on viewFilter
  switch (viewFilter) {
    case 'most-risky':
      rootNodes.sort(compareByAlertsAndActivity);
      break;
    case 'recent-alerts':
      rootNodes.sort((a, b) => {
        return getDateProp(b, 'mostRecentAlertDate') - getDateProp(a, 'mostRecentAlertDate') ||
          compareByAlertsAndActivity(a, b);
      });
      break;
    case 'most-active':
      rootNodes.sort((a, b) => {
        return getNumberProp(b, 'activity') - getNumberProp(a, 'activity') ||
          compareByAlertsAndActivity(a, b);
      });
      break;
    case 'recently-active':
      rootNodes.sort((a, b) => {
        return getDateProp(b, 'lastActivity') - getDateProp(a, 'lastActivity') ||
          getNumberProp(b, 'activity') - getNumberProp(a, 'activity') ||
          compareByAlertsAndActivity(a, b);
      });
      break;
    case 'critical':
      rootNodes = rootNodes
        .filter(node => getNumberProp(node, 'criticalAlertCount') > 0)
        .sort((a, b) => {
          return getNumberProp(b, 'criticalAlertCount') - getNumberProp(a, 'criticalAlertCount') ||
            compareByAlertsAndActivity(a, b);
        });
      break;
    default:
      break;
  }

  return rootNodes.map(n => n.props?.graphId).filter(Boolean) as string[];
};

// Helper function to filter and sort nodes for security questions (frontend-only processing)
const getSecurityQuestionNodes = (
  nodes: GraphNode[],
  questionIndex: number
): GraphNode[] => {
  // Get all root nodes (users, SAs, resources)
  const rootNodes = nodes.filter(n => {
    const isRootUser = n.type === 'user' && n.props?.graphId?.startsWith('user-');
    const isRootSA = n.type === 'service_account' && n.props?.graphId?.startsWith('sa-');
    const isRootResource = n.type === 'resource' && n.props?.graphId?.startsWith('resource-center-');
    return isRootUser || isRootSA || isRootResource;
  });

  switch (questionIndex) {
    case 1: // Most Risky Entities - sort by alert count (highest first)
      return [...rootNodes].sort(compareByAlertsAndActivity);

    case 2: // Dormant Users - show only users with zero activity
      return rootNodes.filter(n =>
        n.type === 'user' && (n.props?.activity === 0 || n.props?.activity === undefined)
      );

    case 3: // Dormant Service Accounts/IAM Roles - show only SAs with zero activity
      return rootNodes.filter(n =>
        n.type === 'service_account' && (n.props?.activity === 0 || n.props?.activity === undefined)
      );

    case 4: // Most Active Entities - sort by activity (highest first)
      return [...rootNodes].sort((a, b) => {
        const aActivity = a.props?.activity || 0;
        const bActivity = b.props?.activity || 0;
        return bActivity - aActivity;
      });

    case 5: // Most Used Resources - sort resources by activity (highest first)
      return rootNodes
        .filter(n => n.type === 'resource')
        .sort((a, b) => {
          const aActivity = a.props?.activity || 0;
          const bActivity = b.props?.activity || 0;
          return bActivity - aActivity;
        });

    case 6: // Over Privileged Accounts - sort by roles count (highest first)
      return [...rootNodes]
        .filter(n => n.type === 'user' || n.type === 'service_account')
        .sort((a, b) => {
          const aRoles = a.props?.roleCount || 0;
          const bRoles = b.props?.roleCount || 0;
          return bRoles - aRoles;
        });

    case 7: // Recent Alerts - sort by most recent alert createdAt
      return [...rootNodes].sort((a, b) => {
        const aDate = a.props?.mostRecentAlertDate ? new Date(a.props.mostRecentAlertDate).getTime() : 0;
        const bDate = b.props?.mostRecentAlertDate ? new Date(b.props.mostRecentAlertDate).getTime() : 0;
        return bDate - aDate;
      });

    default:
      return rootNodes;
  }
};

export const useGraphStore = create<GraphStore>((set, get) => ({
  nodes: [],
  edges: [],
  allNodes: [],
  allEdges: [],
  selectedNodes: new Set(),
  selectedEdges: new Set(),
  inspectorOpen: true,
  viewFilterOpen: true,
  viewFilter: 'most-risky',
  theme: 'light',
  activeConnectionType: 'users',
  activeConnectionSubFilter: null,
  expandedNodes: new Set(),
  collapsedChildren: new Map(),
  currentGraphPage: 0,
  maxGraphsPerPage: 4,
  totalAvailableGraphs: 0,
  focusMode: true,
  focusedGraphId: null,
  activeExpansionPanel: null,
  promotedChildren: new Map(),
  // Search state
  searchQuery: '',
  searchResults: [],
  highlightedNodes: new Set(),
  isSearchFocused: false,
  triggerZoomToNode: null,
  // Security questions state
  queryResults: null,
  activeSecurityQuestion: null,
  // Mode state
  activeMode: 'activity',
  isModeLoading: false,
  // Correlations & Blast Radius raw data
  correlationsData: [],
  blastRadiusData: [],
  // Persisted activity nodes
  activityAllNodes: [],
  activityAllEdges: [],
  // Beautify state
  triggerBeautify: 0,
  isBeautified: false,
  filters: {
    providers: ['gcp'],
    nodeTypes: ['user', 'service_account', 'resource', 'role', 'permission', 'key', 'token', 'anomaly'],
    riskThreshold: 0,
    timeRange: {
      from: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      to: new Date().toISOString(),
    },
    query: '',
  },

  setGraphData: (nodes, edges, queryResults) => {
    const { maxGraphsPerPage, viewFilter } = get();

    // Determine initial filter type based on what ROOT nodes are available
    const hasUsers = nodes.some(n => n.type === 'user' && n.props?.graphId?.startsWith('user-'));
    const hasSA = nodes.some(n => n.type === 'service_account' && n.props?.graphId?.startsWith('sa-'));
    const hasResources = nodes.some(n => n.type === 'resource' && n.props?.graphId?.startsWith('resource-center-'));

    let initialType = 'users';
    if (!hasUsers && hasSA) initialType = 'sa';
    else if (!hasUsers && !hasSA && hasResources) initialType = 'resources';

    // Get sorted graph IDs based on viewFilter
    const allGraphIds = getSortedGraphIds(nodes, initialType, viewFilter);

    // Apply pagination from the start
    const visibleGraphIds = new Set(allGraphIds.slice(0, maxGraphsPerPage));
    const filteredNodes = nodes.filter(n => n.props?.graphId && visibleGraphIds.has(n.props.graphId));
    const filteredEdges = edges.filter(e => e.props?.graphId && visibleGraphIds.has(e.props.graphId));

    set({
      nodes: filteredNodes,
      edges: filteredEdges,
      allNodes: nodes,
      allEdges: edges,
      // Persist activity data for reuse in correlation/blast modes
      activityAllNodes: nodes,
      activityAllEdges: edges,
      currentGraphPage: 0,
      totalAvailableGraphs: allGraphIds.length,
      activeConnectionType: initialType,
      activeConnectionSubFilter: null,
      queryResults: queryResults || null,
      activeSecurityQuestion: null,
      focusMode: true,
      focusedGraphId: null,
      expandedNodes: new Set(),
      collapsedChildren: new Map(),
      promotedChildren: new Map(),
      activeExpansionPanel: null,
      selectedNodes: new Set(),
      selectedEdges: new Set(),
    });
  },

  selectNode: (nodeId) => {
    const selectedNodes = new Set(get().selectedNodes);
    // Don't deselect if clicking the same node - keep it selected
    if (!selectedNodes.has(nodeId)) {
      selectedNodes.clear();
      selectedNodes.add(nodeId);
    }
    set({ selectedNodes, inspectorOpen: true });
  },

  selectEdge: (edgeId) => {
    const selectedEdges = new Set(get().selectedEdges);
    if (selectedEdges.has(edgeId)) {
      selectedEdges.delete(edgeId);
    } else {
      selectedEdges.clear();
      selectedEdges.add(edgeId);
    }
    set({ selectedEdges, inspectorOpen: selectedEdges.size > 0 });
  },

  clearSelection: () => set({ selectedNodes: new Set(), selectedEdges: new Set() }),

  toggleInspector: () => set((state) => ({ inspectorOpen: !state.inspectorOpen })),

  toggleViewFilter: () => set((state) => ({ viewFilterOpen: !state.viewFilterOpen })),

  setViewFilter: (filter) => {
    const { allNodes, allEdges, activeConnectionType, activeConnectionSubFilter, maxGraphsPerPage } = get();

    // Get sorted graph IDs based on new filter
    const allGraphIds = getSortedGraphIds(allNodes, activeConnectionType, filter, activeConnectionSubFilter || undefined);

    // Reset to first page with new sort order
    const visibleGraphIds = new Set(allGraphIds.slice(0, maxGraphsPerPage));
    const filteredNodes = allNodes.filter(n => n.props?.graphId && visibleGraphIds.has(n.props.graphId));
    const filteredEdges = allEdges.filter(e => e.props?.graphId && visibleGraphIds.has(e.props.graphId));

    set({
      viewFilter: filter,
      nodes: filteredNodes,
      edges: filteredEdges,
      currentGraphPage: 0,
      totalAvailableGraphs: allGraphIds.length,
      activeSecurityQuestion: null,
      focusMode: true,
      focusedGraphId: null,
      expandedNodes: new Set(),
      collapsedChildren: new Map(),
      promotedChildren: new Map(),
      activeExpansionPanel: null,
      selectedNodes: new Set(),
      selectedEdges: new Set(),
    });
  },

  setFilters: (filters) => set((state) => ({
    filters: { ...state.filters, ...filters }
  })),

  toggleProvider: (provider) => {
    const { filters } = get();
    const providers = filters.providers.includes(provider)
      ? filters.providers.filter(p => p !== provider)
      : [...filters.providers, provider];
    set({ filters: { ...filters, providers } });
  },

  toggleNodeType: (nodeType) => {
    const { filters } = get();
    const nodeTypes = filters.nodeTypes.includes(nodeType)
      ? filters.nodeTypes.filter(t => t !== nodeType)
      : [...filters.nodeTypes, nodeType];
    set({ filters: { ...filters, nodeTypes } });
  },

  toggleTheme: () => {
    const newTheme = get().theme === 'light' ? 'dark' : 'light';
    set({ theme: newTheme });

    // Update document class
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  },

  setActiveConnectionType: (type: string, subFilter?: string) => {
    const { allNodes, allEdges, maxGraphsPerPage, viewFilter } = get();

    // Get sorted graph IDs based on viewFilter
    const allGraphIds = getSortedGraphIds(allNodes, type, viewFilter, subFilter);

    // Paginate: show only maxGraphsPerPage graphs at a time
    const startIndex = 0; // Reset to first page
    const endIndex = Math.min(startIndex + maxGraphsPerPage, allGraphIds.length);
    const visibleGraphIds = new Set(allGraphIds.slice(startIndex, endIndex));

    const filteredNodes = allNodes.filter(n =>
      n.props?.graphId && visibleGraphIds.has(n.props.graphId)
    );
    const filteredEdges = allEdges.filter(e =>
      e.props?.graphId && visibleGraphIds.has(e.props.graphId)
    );

    set({
      nodes: filteredNodes,
      edges: filteredEdges,
      activeConnectionType: type,
      activeConnectionSubFilter: subFilter || null,
      currentGraphPage: 0,
      totalAvailableGraphs: allGraphIds.length,
      focusMode: true,
      focusedGraphId: null,
      expandedNodes: new Set(),
      collapsedChildren: new Map(),
      promotedChildren: new Map(),
      activeExpansionPanel: null,
      selectedNodes: new Set(),
      selectedEdges: new Set(),
    });
  },

  nextGraphPage: () => {
    const { allNodes, allEdges, activeConnectionType, activeConnectionSubFilter, currentGraphPage, maxGraphsPerPage, viewFilter } = get();

    // Get sorted graph IDs based on viewFilter
    const allGraphIds = getSortedGraphIds(allNodes, activeConnectionType, viewFilter, activeConnectionSubFilter || undefined);

    const newPage = currentGraphPage + 1;
    const startIndex = newPage * maxGraphsPerPage;

    if (startIndex >= allGraphIds.length) return; // Can't go further

    const endIndex = Math.min(startIndex + maxGraphsPerPage, allGraphIds.length);
    const visibleGraphIds = new Set(allGraphIds.slice(startIndex, endIndex));

    const filteredNodes = allNodes.filter(n =>
      n.props?.graphId && visibleGraphIds.has(n.props.graphId)
    );
    const filteredEdges = allEdges.filter(e =>
      e.props?.graphId && visibleGraphIds.has(e.props.graphId)
    );

    set({ nodes: filteredNodes, edges: filteredEdges, currentGraphPage: newPage });
  },

  previousGraphPage: () => {
    const { allNodes, allEdges, activeConnectionType, activeConnectionSubFilter, currentGraphPage, maxGraphsPerPage, viewFilter } = get();

    if (currentGraphPage === 0) return; // Already at first page

    // Get sorted graph IDs based on viewFilter
    const allGraphIds = getSortedGraphIds(allNodes, activeConnectionType, viewFilter, activeConnectionSubFilter || undefined);

    const newPage = currentGraphPage - 1;
    const startIndex = newPage * maxGraphsPerPage;
    const endIndex = Math.min(startIndex + maxGraphsPerPage, allGraphIds.length);
    const visibleGraphIds = new Set(allGraphIds.slice(startIndex, endIndex));

    const filteredNodes = allNodes.filter(n =>
      n.props?.graphId && visibleGraphIds.has(n.props.graphId)
    );
    const filteredEdges = allEdges.filter(e =>
      e.props?.graphId && visibleGraphIds.has(e.props.graphId)
    );

    set({ nodes: filteredNodes, edges: filteredEdges, currentGraphPage: newPage });
  },

  canGoNext: () => {
    const { allNodes, activeConnectionType, activeConnectionSubFilter, currentGraphPage, maxGraphsPerPage, viewFilter } = get();

    const allGraphIds = getSortedGraphIds(allNodes, activeConnectionType, viewFilter, activeConnectionSubFilter || undefined);
    const totalGraphs = allGraphIds.length;

    return (currentGraphPage + 1) * maxGraphsPerPage < totalGraphs;
  },

  canGoPrevious: () => {
    return get().currentGraphPage > 0;
  },

  expandNode: (nodeId) => {
    const expandedNodes = new Set(get().expandedNodes);
    const collapsedChildren = new Map(get().collapsedChildren);

    // Mark node as collapsed initially (will show +X more if >4 children)
    if (!collapsedChildren.has(nodeId)) {
      collapsedChildren.set(nodeId, []); // Empty array means "not yet fully expanded"
    }

    expandedNodes.add(nodeId);
    set({ expandedNodes, collapsedChildren, inspectorOpen: true });
  },

  collapseAll: () => {
    const { focusMode, focusedGraphId } = get();
    if (focusMode && focusedGraphId) {
      // In focus mode, restore all nodes
      set({
        expandedNodes: new Set(),
        collapsedChildren: new Map(),
        selectedNodes: new Set(),
        focusedGraphId: null,
        activeExpansionPanel: null,
        promotedChildren: new Map(),
      });
    } else {
      set({
        expandedNodes: new Set(),
        collapsedChildren: new Map(),
        selectedNodes: new Set(),
        activeExpansionPanel: null,
        promotedChildren: new Map(),
      });
    }
  },

  collapseStep: (nodeId) => {
    const promotedChildren = new Map(get().promotedChildren);
    const expandedNodes = new Set(get().expandedNodes);

    // Remove promoted children of this node
    promotedChildren.delete(nodeId);

    // Also remove this node from expanded nodes
    expandedNodes.delete(nodeId);

    // Recursively collapse all descendants
    const collapseDescendants = (parentId: string) => {
      const children = promotedChildren.get(parentId);
      if (children) {
        children.forEach(childId => {
          expandedNodes.delete(childId);
          promotedChildren.delete(childId);
          collapseDescendants(childId);
        });
      }
    };

    // Get the children before we delete them
    const children = promotedChildren.get(nodeId);
    if (children) {
      children.forEach(childId => {
        collapseDescendants(childId);
      });
    }

    set({
      promotedChildren,
      expandedNodes,
      activeExpansionPanel: null,
    });
  },

  expandCollapsedGroup: (parentId) => {
    const collapsedChildren = new Map(get().collapsedChildren);
    collapsedChildren.delete(parentId); // Remove from map = show all children
    set({ collapsedChildren });
  },

  openExpansionPanel: (parentId, childIds) => {
    set({
      activeExpansionPanel: { parentId, childIds },
      inspectorOpen: true,
    });
  },

  closeExpansionPanel: () => {
    set({ activeExpansionPanel: null });
  },

  promoteChild: (parentId, childId) => {
    const promotedChildren = new Map(get().promotedChildren);
    const expandedNodes = new Set(get().expandedNodes);

    if (!promotedChildren.has(parentId)) {
      promotedChildren.set(parentId, new Set());
    }
    promotedChildren.get(parentId)!.add(childId);

    // Also expand the parent so the child appears in the graph
    expandedNodes.add(parentId);

    set({
      promotedChildren,
      expandedNodes,
      activeExpansionPanel: null, // Close panel after selecting
    });
  },

  toggleFocusMode: () => {
    const newFocusMode = !get().focusMode;
    set({
      focusMode: newFocusMode,
      focusedGraphId: newFocusMode ? null : null // Reset focused graph when toggling
    });
  },

  setFocusedGraph: (graphId) => {
    set({ focusedGraphId: graphId });
  },

  // Search actions
  setSearchQuery: (query: string) => {
    const { allNodes, activeConnectionType, activeConnectionSubFilter } = get();

    if (!query.trim()) {
      set({
        searchQuery: '',
        searchResults: [],
        highlightedNodes: new Set(),
        triggerZoomToNode: null,
      });
      return;
    }

    // Determine which node types to search based on activeConnectionType
    const allowedTypes: string[] = [];
    if (activeConnectionType === 'users') {
      allowedTypes.push('user');
    } else if (activeConnectionType === 'sa') {
      allowedTypes.push('service_account');
    } else if (activeConnectionType === 'resources') {
      allowedTypes.push('resource');
    }

    const lowerQuery = query.toLowerCase();
    const seenLabels = new Set<string>();
    const matchingNodeIds: string[] = [];

    allNodes.forEach(node => {
      // Skip permission nodes and alert hierarchy nodes
      if (node.type === 'permission') return;
      if (node.props?.isAlertHierarchy) return;

      // Filter by allowed types based on active connection type
      if (allowedTypes.length > 0 && !allowedTypes.includes(node.type)) return;

      // For resources, only match root resource nodes (resource-center-)
      if (activeConnectionType === 'resources' && node.type === 'resource') {
        if (!node.id.startsWith('resource-center-')) return;
      }

      if (activeConnectionType === 'sa' && node.type === 'service_account') {
        if (!node.props?.graphId?.startsWith('sa-')) return;
        if (activeConnectionSubFilter === 'user-managed' && node.props?.isUserManaged !== true) return;
        if (activeConnectionSubFilter === 'cloud-managed' && node.props?.isUserManaged !== false) return;
      }

      // Deduplicate by label+email combination
      const uniqueKey = `${node.label}-${node.props?.email || ''}`;
      if (seenLabels.has(uniqueKey)) return;

      // Check if matches
      const matches =
        node.label.toLowerCase().includes(lowerQuery) ||
        node.type.toLowerCase().includes(lowerQuery) ||
        node.id.toLowerCase().includes(lowerQuery) ||
        (node.props?.email?.toLowerCase().includes(lowerQuery) ?? false);

      if (matches) {
        seenLabels.add(uniqueKey);
        matchingNodeIds.push(node.id);
      }
    });

    set({
      searchQuery: query,
      searchResults: matchingNodeIds,
      highlightedNodes: new Set(matchingNodeIds),
    });
  },

  setSearchFocused: (focused: boolean) => {
    if (!focused) {
      // Clear highlights when search loses focus
      set({ isSearchFocused: focused, highlightedNodes: new Set() });
    } else {
      set({ isSearchFocused: focused });
    }
  },

  clearSearch: () => {
    set({
      searchQuery: '',
      searchResults: [],
      highlightedNodes: new Set(),
      isSearchFocused: false,
      triggerZoomToNode: null,
    });
  },

  zoomToHighlightedNodes: () => {
    const { highlightedNodes, nodes } = get();
    if (highlightedNodes.size === 0) return;

    // Find first highlighted node that's visible
    const firstHighlighted = Array.from(highlightedNodes).find(id =>
      nodes.some(n => n.id === id)
    );

    if (firstHighlighted) {
      set({ triggerZoomToNode: firstHighlighted });
    }
  },

  setTriggerZoomToNode: (nodeId: string | null) => {
    set({ triggerZoomToNode: nodeId });
  },

  navigateToNode: (nodeId: string) => {
    const { allNodes, allEdges, activeConnectionType, activeConnectionSubFilter, maxGraphsPerPage, viewFilter } = get();

    // Find the node
    const targetNode = allNodes.find(n => n.id === nodeId);
    if (!targetNode) return;

    // Get the graphId of the target node
    const targetGraphId = targetNode.props?.graphId;
    if (!targetGraphId) return;

    // Use the same sorted graph IDs as the rest of the app (respecting viewFilter)
    const allGraphIds = getSortedGraphIds(allNodes, activeConnectionType, viewFilter, activeConnectionSubFilter || undefined);

    // Find which page this graph is on
    const graphIndex = allGraphIds.indexOf(targetGraphId);
    if (graphIndex === -1) return;

    const targetPage = Math.floor(graphIndex / maxGraphsPerPage);

    // Navigate to that page
    const startIndex = targetPage * maxGraphsPerPage;
    const endIndex = Math.min(startIndex + maxGraphsPerPage, allGraphIds.length);
    const visibleGraphIds = new Set(allGraphIds.slice(startIndex, endIndex));

    const filteredNodes = allNodes.filter(n =>
      n.props?.graphId && visibleGraphIds.has(n.props.graphId)
    );
    const filteredEdges = allEdges.filter(e =>
      e.props?.graphId && visibleGraphIds.has(e.props.graphId)
    );

    // Update highlighted nodes to only include the target and trigger zoom
    set({
      nodes: filteredNodes,
      edges: filteredEdges,
      currentGraphPage: targetPage,
      highlightedNodes: new Set([nodeId]),
      selectedNodes: new Set([nodeId]),
      inspectorOpen: true,
      triggerZoomToNode: nodeId, // Trigger zoom to the selected node
    });
  },

  // Security questions actions - now using frontend-only processing
  applySecurityQuestion: (questionIndex: number) => {
    const { allNodes, allEdges, maxGraphsPerPage } = get();

    // Use frontend-only processing to filter and sort nodes
    const matchingRootNodes = getSecurityQuestionNodes(allNodes, questionIndex);

    if (matchingRootNodes.length === 0) {
      console.log(`No results for question ${questionIndex}`);
      set({
        nodes: [],
        edges: [],
        activeSecurityQuestion: questionIndex,
        currentGraphPage: 0,
        totalAvailableGraphs: 0,
      });
      return;
    }

    // Get unique graph IDs from matching nodes (in sorted order)
    const matchingGraphIds = matchingRootNodes
      .map(n => n.props?.graphId)
      .filter(Boolean) as string[];

    // Determine the connection type based on the question
    let connectionType = 'users';
    if (questionIndex === 2) {
      connectionType = 'users'; // Dormant Users
    } else if (questionIndex === 3) {
      connectionType = 'sa'; // Dormant Service Accounts
    } else if (questionIndex === 5) {
      connectionType = 'resources'; // Most Used Resources
    } else {
      // For mixed questions (1, 4, 6, 7), check what types we have
      const hasUsers = matchingRootNodes.some(n => n.type === 'user');
      const hasSAs = matchingRootNodes.some(n => n.type === 'service_account');
      const hasResources = matchingRootNodes.some(n => n.type === 'resource');

      if (hasUsers) connectionType = 'users';
      else if (hasSAs) connectionType = 'sa';
      else if (hasResources) connectionType = 'resources';
    }

    // Apply pagination
    const visibleGraphIds = new Set(matchingGraphIds.slice(0, maxGraphsPerPage));

    const filteredNodes = allNodes.filter(n =>
      n.props?.graphId && visibleGraphIds.has(n.props.graphId)
    );
    const filteredEdges = allEdges.filter(e =>
      e.props?.graphId && visibleGraphIds.has(e.props.graphId)
    );

    set({
      nodes: filteredNodes,
      edges: filteredEdges,
      activeSecurityQuestion: questionIndex,
      activeConnectionType: connectionType,
      activeConnectionSubFilter: null,
      currentGraphPage: 0,
      totalAvailableGraphs: matchingGraphIds.length,
      expandedNodes: new Set(),
      collapsedChildren: new Map(),
      promotedChildren: new Map(),
      selectedNodes: new Set(),
    });
  },

  clearSecurityQuestion: () => {
    const { allNodes, allEdges, maxGraphsPerPage, viewFilter } = get();

    // Reset to default users view
    const allGraphIds = getSortedGraphIds(allNodes, 'users', viewFilter);
    const visibleGraphIds = new Set(allGraphIds.slice(0, maxGraphsPerPage));

    const filteredNodes = allNodes.filter(n =>
      n.props?.graphId && visibleGraphIds.has(n.props.graphId)
    );
    const filteredEdges = allEdges.filter(e =>
      e.props?.graphId && visibleGraphIds.has(e.props.graphId)
    );

    set({
      nodes: filteredNodes,
      edges: filteredEdges,
      activeSecurityQuestion: null,
      activeConnectionType: 'users',
      activeConnectionSubFilter: null,
      currentGraphPage: 0,
      totalAvailableGraphs: allGraphIds.length,
      expandedNodes: new Set(),
      collapsedChildren: new Map(),
      promotedChildren: new Map(),
      selectedNodes: new Set(),
    });
  },

  setActiveMode: (mode) => set({
    activeMode: mode,
    // Reset focus mode and collapse when switching modes
    // For correlations/blast-radius, disable focus mode since graphIds don't apply
    focusMode: mode === 'activity',
    focusedGraphId: null,
    expandedNodes: new Set(),
    promotedChildren: new Map(),
    collapsedChildren: new Map(),
    activeExpansionPanel: null,
    selectedNodes: new Set(),
    selectedEdges: new Set(),
    isBeautified: false,
  }),
  setModeLoading: (loading) => set({ isModeLoading: loading }),
  setCorrelationsData: (data) => set({ correlationsData: data }),
  setBlastRadiusData: (data) => set({ blastRadiusData: data }),
  setModeGraphData: (nodes, edges) => set({
    nodes,
    edges,
    allNodes: nodes,
    allEdges: edges,
    currentGraphPage: 0,
    expandedNodes: new Set(),
    promotedChildren: new Map(),
    activeExpansionPanel: null,
    selectedNodes: new Set(),
    selectedEdges: new Set(),
    focusedGraphId: null,
  }),
  getActivityAllNodes: () => get().activityAllNodes,
  triggerBeautifyLayout: () => set((state) => ({ triggerBeautify: state.triggerBeautify + 1, isBeautified: true })),
  setBeautified: (val) => set({ isBeautified: val }),
}));
